// =====================================================================
// src/modulos/endereco.js — país, município e endereço da ficha (0070)
//
// Um componente só para os três lugares que preenchem a ficha — o modal
// de Hóspedes, a ficha do balcão (Check-in) e a página do hóspede (link) —
// porque a regra é uma só (a do banco, `app.endereco_conferido`) e três
// cópias divergiriam na primeira mudança:
//   · o país de residência vem primeiro: ele decide o resto;
//   · mora no Brasil → UF, depois o MUNICÍPIO da lista do IBGE, o CEP de 8
//     dígitos e o bairro;
//   · mora fora → a cidade digitada e o código postal como for;
//   · logradouro e número, separados (a API os pede assim).
//
// O componente escreve no objeto `f` que recebe (o estado do formulário)
// e se redesenha sozinho quando o país ou a UF mudam — o resto da tela
// não precisa saber. A lista de municípios só é baixada quando alguém
// escolhe o Brasil (lib/ibge.js).
//
// O TEXTO ANTIGO (o que foi digitado antes da 0070 e não converteu)
// aparece em cinza embaixo do campo que o substitui: "antes: Americana".
// =====================================================================
import { el } from '../ui.js';
import { BRASIL, UFS, opcoesDePaises, carregarMunicipios, municipiosDaUF, municipio as achar } from '../lib/ibge.js';

const PT = {
  nacionalidade: 'Nacionalidade', residencia: 'País onde mora', uf: 'UF', municipio: 'Município', cidade: 'Cidade',
  cep: 'CEP', codigo_postal: 'Código postal', logradouro: 'Logradouro', logradouro_dica: 'Rua, avenida, quadra…', numero: 'Número',
  numero_dica: 'ou S/N', bairro: 'Bairro', complemento: 'Complemento', complemento_dica: 'Apto, bloco',
  escolha: '—', escolha_uf: 'escolha a UF primeiro', carregando: 'carregando os municípios…',
  erro_municipios: 'A lista de municípios não carregou. Tente de novo.', tentar: 'Tentar de novo', antes: 'antes: {v}',
};
const traduz = t => (k, v = {}) => { const s = (t && t(k, v)) || PT[k] || k; return String(s).replace(/\{(\w+)\}/g, (_, x) => (x in v ? v[x] : `{${x}}`)); };

// dentro da linha do campo (`solta` falso) ou abaixo de uma linha de dois campos
const linhaAntes = (tt, v, solta = false) => (v ? el('div', { class: 'usu-email', style: { margin: solta ? '-6px 0 10px' : '4px 0 0', color: '#9ca3af' }, dataset: { antes: '1' } }, tt('antes', { v })) : null);

// O seletor de país (a nacionalidade usa este também).
export function seletorPais(f, campo, rotulo, { id, idioma = 'pt', antes = '', aoMudar = null, t = null } = {}) {
  const tt = traduz(t);
  const s = el('select', { class: 'input', id,
    onchange: ev => { f[campo] = ev.target.value; if (aoMudar) aoMudar(ev.target.value); } },
    el('option', { value: '' }, tt('escolha')),
    opcoesDePaises(idioma).map(([v, n]) => el('option', { value: v, selected: f[campo] === v }, n)));
  // o "antes: …" mora DENTRO da linha do campo: é dele
  return el('div', { class: 'form-row' }, el('label', { class: 'label', for: id }, rotulo), s, linhaAntes(tt, antes));
}

// O bloco de residência. `id(campo)` monta o id de cada campo (cada tela
// tem o seu prefixo). `antigo` é `textoAntigo(h)` de lib/hospedes.js.
export function blocoResidencia(f, { id, idioma = 'pt', antigo = {}, t = null, carregar = carregarMunicipios, extra = null } = {}) {
  const tt = traduz(t);
  // fieldset: é um grupo de campos (o endereço), e não um bloco com título
  const caixa = el('fieldset', { dataset: { bloco: 'residencia' }, style: { border: '0', padding: '0', margin: '0', minWidth: '0' } });
  const input = (campo, rotulo, attrs = {}) => el('div', { class: 'form-row' },
    el('label', { class: 'label', for: id(campo) }, rotulo),
    el('input', { class: 'input', id: id(campo), value: f[campo] || '', autocomplete: 'off', ...attrs, oninput: ev => { f[campo] = ev.target.value; } }));
  const dupla = (a, b, colunas = '1fr 1fr') => el('div', { style: { display: 'grid', gridTemplateColumns: colunas, gap: '10px' } }, a, b);

  let indice = null, erro = null, pedindo = false;
  function desenhar() {
    const noBrasil = f.residencia_pais === BRASIL;
    const fora = !!f.residencia_pais && !noBrasil;
    const partes = [
      seletorPais(f, 'residencia_pais', tt('residencia'), { id: id('residencia_pais'), idioma, antes: antigo.residencia_pais, t,
        aoMudar: v => {
          // trocar de país zera o que era do outro lado
          if (v === BRASIL) f.cidade = ''; else { f.municipio_id = ''; f.uf = ''; }
          desenhar();
        } }),
    ];
    if (noBrasil) {
      const uf = el('select', { class: 'input', id: id('uf'), onchange: ev => { f.uf = ev.target.value; f.municipio_id = ''; desenhar(); } },
        el('option', { value: '' }, tt('escolha')), UFS.map(u => el('option', { value: u, selected: String(f.uf || '').toUpperCase() === u }, u)));
      let mun;
      if (erro) {
        mun = el('div', { class: 'form-row' }, el('label', { class: 'label' }, tt('municipio')),
          el('div', { class: 'alert alert-error', id: id('municipio_erro') }, tt('erro_municipios'), ' ',
            el('button', { type: 'button', class: 'btn btn-secondary btn-sm', onclick: () => { erro = null; desenhar(); } }, tt('tentar'))));
      } else if (!indice) {
        mun = el('div', { class: 'form-row' }, el('label', { class: 'label' }, tt('municipio')),
          el('div', { class: 'usu-email', id: id('municipio_carregando') }, tt('carregando')));
        if (!pedindo) { pedindo = true; carregar().then(i => { pedindo = false; indice = i; desenhar(); }, () => { pedindo = false; erro = true; desenhar(); }); }
      } else {
        // o município já gravado abre a lista no estado dele
        const atual = achar(indice, f.municipio_id);
        if (atual && !f.uf) f.uf = atual.uf;
        if (atual) uf.value = atual.uf;
        const lista = municipiosDaUF(indice, f.uf);
        mun = el('div', { class: 'form-row' }, el('label', { class: 'label', for: id('municipio_id') }, tt('municipio')),
          el('select', { class: 'input', id: id('municipio_id'), disabled: !f.uf, onchange: ev => { f.municipio_id = ev.target.value; } },
            el('option', { value: '' }, f.uf ? tt('escolha') : tt('escolha_uf')),
            lista.map(m => el('option', { value: String(m.id), selected: String(f.municipio_id) === String(m.id) }, m.nome))));
      }
      partes.push(dupla(el('div', { class: 'form-row' }, el('label', { class: 'label', for: id('uf') }, tt('uf')), uf), mun, '1fr 3fr'));
      partes.push(linhaAntes(tt, antigo.municipio_id, true));
      partes.push(dupla(input('cep', tt('cep'), { inputmode: 'numeric', placeholder: '00000-000' }), input('bairro', tt('bairro'))));
    } else if (fora) {
      partes.push(dupla(input('cidade', tt('cidade')), input('cep', tt('codigo_postal'))));
    } else {
      partes.push(linhaAntes(tt, antigo.municipio_id, true));
    }
    if (f.residencia_pais) {
      partes.push(dupla(input('logradouro', tt('logradouro'), { placeholder: tt('logradouro_dica'), autocomplete: 'address-line1' }),
        input('numero_endereco', tt('numero'), { placeholder: tt('numero_dica') }), '3fr 1fr'));
      partes.push(linhaAntes(tt, antigo.logradouro, true));
      partes.push(fora
        ? dupla(input('complemento', tt('complemento'), { placeholder: tt('complemento_dica') }), input('bairro', tt('bairro')))
        : input('complemento', tt('complemento'), { placeholder: tt('complemento_dica') }));
    } else {
      partes.push(linhaAntes(tt, antigo.logradouro, true));
    }
    if (extra) partes.push(extra);
    caixa.replaceChildren(...partes.filter(Boolean));
  }
  desenhar();
  return caixa;
}
